import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { SecretsCountResponse } from "@protos/secrets/v1/secrets";
import { authGet } from "@shared/api";
import { timeLookbackRangeToSince } from "@shared/utils";

import { useHideHistoricalFindings } from "../useHideHistoricalFindings";
import { useOrgAssertDefined } from "../useOrgAssertDefined";
import { useTimeLookbackRange } from "../useTimeLookbackRange";

export const fetchSecretsCount = async (
  deployment_id: number,
  queryString: string
): Promise<SecretsCountResponse> => {
  const resp = await authGet<SecretsCountResponse>(
    `/api/secrets/deployments/${deployment_id}/findings/count?${queryString}`
  );

  return SecretsCountResponse.fromJSON(resp);
};

/**
 * @param fetchTotalOpen If true, fetches the total number of open secrets. If false, fetches the number of
 * open secrets using the search params in the URL. Defaults to false.
 * @returns react-query hook to fetch the number of secrets for the current org.
 */
export const useSecretsCount = (fetchTotalOpen: boolean = false) => {
  const org = useOrgAssertDefined();
  const [tlr] = useTimeLookbackRange();
  const [hideHistorical] = useHideHistoricalFindings();
  const history = useHistory();
  const params = fetchTotalOpen
    ? new URLSearchParams()
    : new URLSearchParams(history.location.search);

  if (!params.has("status")) {
    params.set("status", "open");
  }
  if (tlr) {
    params.set("since", String(timeLookbackRangeToSince(tlr)));
  }
  if (hideHistorical) {
    params.set("historical", "false");
  }
  if (params.get("limit")) {
    params.delete("limit");
  }

  const stringParams = params.toString();

  return useQuery({
    queryKey: ["SecretsCount", org.id, stringParams, tlr, org],

    queryFn: () => fetchSecretsCount(org.id, stringParams).then((res) => res),

    enabled: stringParams.length > 0,
  });
};
